import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/shared/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  savePushSubscriptionForFactory,
  removePushSubscriptionForUser,
} from '@/core/api';

interface PushNotificationState {
  isSupported: boolean;
  permission: NotificationPermission | 'default';
  subscription: PushSubscription | null;
  isLoading: boolean;
}

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || 'BKKx-yUD1hmZLC88bIUZTkcUxe4M4e6PrWgknoW816CmqgOuR4cTUO-6pakKKm6FXjBcCzcSk6eHuVY844_GY_8';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function saveSubscriptionToDatabase(
  subscription: PushSubscription,
  factoryId: string
): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const subscriptionJson = subscription.toJSON();
    const keys = subscriptionJson.keys as { p256dh: string; auth: string } | undefined;
    
    if (!keys) return false;

    return savePushSubscriptionForFactory(
      user.id,
      factoryId,
      subscriptionJson.endpoint!,
      keys
    );
  } catch (error) {
    console.error('Error saving subscription to database:', error);
    return false;
  }
}

async function removeSubscriptionFromDatabase(endpoint: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    return removePushSubscriptionForUser(user.id, endpoint);
  } catch (error) {
    console.error('Error removing subscription from database:', error);
    return false;
  }
}

export function usePushNotifications() {
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    permission: 'default',
    subscription: null,
    isLoading: true,
  });

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
      
      if (!isSupported) {
        setState(prev => ({ ...prev, isSupported: false, isLoading: false }));
        return;
      }

      const permission = Notification.permission;
      
      // Try to get existing subscription
      let subscription: PushSubscription | null = null;
      try {
        const registration = await navigator.serviceWorker.ready;
        subscription = await registration.pushManager.getSubscription();
      } catch (error) {
        console.error('Error getting subscription:', error);
      }

      setState({
        isSupported: true,
        permission,
        subscription,
        isLoading: false,
      });
    };

    checkSupport();
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async (): Promise<ServiceWorkerRegistration | null> => {
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported) {
      toast({
        title: 'Not Supported',
        description: 'Push notifications are not supported in this browser.',
        variant: 'destructive',
      });
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setState(prev => ({ ...prev, permission }));
      
      if (permission === 'granted') {
        toast({
          title: 'Notifications Enabled',
          description: 'You will now receive push notifications for alerts.',
        });
        return true;
      } else if (permission === 'denied') {
        toast({
          title: 'Notifications Blocked',
          description: 'Please enable notifications in your browser settings.',
          variant: 'destructive',
        });
        return false;
      }
      return false;
    } catch (error) {
      console.error('Error requesting permission:', error);
      return false;
    }
  }, [state.isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<PushSubscription | null> => {
    if (!state.isSupported || state.permission !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return null;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // Register service worker first
      let registration = await navigator.serviceWorker.ready;
      if (!registration) {
        registration = await registerServiceWorker() as ServiceWorkerRegistration;
        if (!registration) {
          throw new Error('Failed to register service worker');
        }
        // Wait for service worker to be ready
        registration = await navigator.serviceWorker.ready;
      }

      // Check for existing subscription
      let subscription = await registration.pushManager.getSubscription();
      
      if (!subscription && VAPID_PUBLIC_KEY) {
        // Create new subscription
        const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey as BufferSource,
        });
        console.log('Push subscription created:', subscription);
      }

      setState(prev => ({ ...prev, subscription, isLoading: false }));
      
      if (subscription) {
        // Store subscription in localStorage
        localStorage.setItem('push_subscription', JSON.stringify(subscription.toJSON()));
        toast({
          title: 'Subscribed',
          description: 'You are now subscribed to push notifications.',
        });
      }

      return subscription;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      setState(prev => ({ ...prev, isLoading: false }));
      toast({
        title: 'Subscription Failed',
        description: 'Failed to subscribe to push notifications. Please try again.',
        variant: 'destructive',
      });
      return null;
    }
  }, [state.isSupported, state.permission, requestPermission, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!state.subscription) return true;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const endpoint = state.subscription.endpoint;
      await state.subscription.unsubscribe();
      localStorage.removeItem('push_subscription');
      
      // Remove from database
      await removeSubscriptionFromDatabase(endpoint);
      
      setState(prev => ({ ...prev, subscription: null, isLoading: false }));
      toast({
        title: 'Unsubscribed',
        description: 'You will no longer receive push notifications.',
      });
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [state.subscription]);

  // Send a local notification (for testing or fallback)
  const sendLocalNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (state.permission !== 'granted') {
      // Fallback to toast
      toast({
        title,
        description: options?.body,
        variant: options?.tag?.includes('critical') ? 'destructive' : 'default',
      });
      return;
    }

    try {
      new Notification(title, {
        icon: '/favicon.jpg',
        badge: '/favicon.jpg',
        requireInteraction: true,
        ...options,
      });
    } catch (error) {
      // Fallback to toast if Notification API fails
      toast({
        title,
        description: options?.body,
      });
    }
  }, [state.permission]);

  // Save subscription to database with factory context
  const saveToDatabase = useCallback(async (factoryId: string): Promise<boolean> => {
    if (!state.subscription) return false;
    return saveSubscriptionToDatabase(state.subscription, factoryId);
  }, [state.subscription]);

  return {
    ...state,
    requestPermission,
    subscribe,
    unsubscribe,
    sendLocalNotification,
    registerServiceWorker,
    saveToDatabase,
  };
}
